import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../../lib/utils";

/**
 * The house button, refitted for this app's reader.
 *
 * Same convention as `apps/portal/src/components/ui/button.tsx` — cva variants
 * composed through `cn()` — because that is what "the same tech stack" buys:
 * a variant added there is a variant that means the same thing here.
 *
 * ── Two deliberate differences from portal's copy ────────────────────────
 *
 * 1. THE SIZE SCALE IS NOT PORTAL'S. Portal's default is `h-10 text-sm` — a
 *    40px target with 14px type, correct for a desktop admin tool. This app's
 *    floor is a 56px target and 17px base type, set in styles.css for a
 *    property agent using one thumb, outdoors, on a mid-range Android. Copying
 *    portal's defaultVariants would shrink every CTA here by 24px on exactly
 *    the device the app exists for — the stack would match and the product
 *    would be worse. Adopting a convention is not adopting its calibration.
 *
 * 2. NO `asChild`/Slot. Portal uses it in this one file, for one case; pulling
 *    @radix-ui/react-slot in for it costs 1.0 KB gzipped against a budget with
 *    13.5 KB of headroom. A link that should look like a button uses
 *    `buttonVariants()` directly, which is the same styling with no runtime.
 */
export const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"bg-primary-500 text-foreground-inverse hover:bg-primary-600 active:bg-primary-700",
				outline:
					"border border-border-default bg-background-elevated text-foreground-default hover:bg-background-muted hover:border-border-strong",
				ghost:
					"text-foreground-default hover:bg-background-muted hover:text-foreground-default",
				link: "text-primary-600 underline-offset-4 hover:underline",
			},
			size: {
				// 64px. The primary action on a screen: sign in, withdraw.
				default: "h-16 rounded-2xl px-6 text-lg [&_svg]:size-5",
				// 56px — the floor from styles.css, never smaller.
				sm: "h-14 rounded-xl px-5 text-base [&_svg]:size-5",
				icon: "size-12 rounded-xl [&_svg]:size-6",
			},
			full: { true: "w-full", false: "" },
		},
		defaultVariants: { variant: "default", size: "default", full: false },
	},
);

export interface ButtonProps
	extends ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, full, type, ...props }, ref) => (
		<button
			ref={ref}
			// Defaulted, not required. A <button> inside a <form> with no type
			// submits it, so an unmarked secondary action ("Change number",
			// "Resend") silently submits the phone step.
			type={type ?? "button"}
			className={cn(buttonVariants({ variant, size, full }), className)}
			{...props}
		/>
	),
);
Button.displayName = "Button";
